/* --------------------------------------------------
                Imports
-------------------------------------------------- */
import { Controller } from "@hotwired/stimulus"
import Choices from "choices.js"

/* --------------------------------------------------
                Choices Controller
  Single integration point for choices.js. Views declare
  behavior via Stimulus values/targets; swap this file if
  the library changes.
-------------------------------------------------- */
export default class extends Controller {
  static targets = ["select", "table", "tableBody", "richTemplate", "simpleTemplate"]

  static values = {
    removeItemButton: { type: Boolean, default: false },
    searchEnabled: { type: Boolean, default: false },
    searchPlaceholder: { type: String, default: "Type to search..." },
    noResultsText: { type: String, default: "No matches found" },
    noChoicesText: { type: String, default: "Start typing to search..." },
    itemSelectText: { type: String, default: "" },
    route: String,
    planroomId: String,
    role: String,
    /** Base path for JSON lookups, e.g. /pages/jobs */
    searchBase: { type: String, default: "/pages/jobs" },
    debounceMs: { type: Number, default: 300 }
  }

  connect() {
    if (this.choices) return

    this.removingFromTable = false
    this.debounceTimeout = null
    this.preSelectedData = new Map()
    this.remoteLoadingBound = false
    this.isLoadingRemote = false
    this.remoteLoadAbortController = null
    this.remoteKeyupHandler = null
    this.showDropdownHandler = null

    if (this.tableSyncEnabled) {
      this.cachePreSelectedRows()
    }

    this.initializeChoices()
    this.setupSelectListeners()

    if (this.tableSyncEnabled) {
      this.toggleEmptyState()
    }

    if (this.hasRouteValue) {
      if (this.element.closest('[id^="modal-"]')) {
        this.bindModalRemoteLoading()
      } else {
        this.setupRemoteLoading()
      }
    }
  }

  disconnect() {
    // Choices.js wraps the <select> in its own DOM, which moves our element.
    // Stimulus reports that move as remove+add and would tear us down here,
    // then immediately re-connect — looping forever. Only run cleanup when
    // the element is actually leaving the document.
    if (this.element.isConnected) return

    this.unbindModalRemoteLoading()
    this.teardownRemoteLoading()
    this.destroyChoicesInstance()
  }

  get tableSyncEnabled() {
    return this.hasTableTarget && this.hasTableBodyTarget
  }

  initializeChoices() {
    this.destroyChoicesInstance()

    this.choices = new Choices(this.selectTarget, {
      closeDropdownOnSelect: true,
      removeItemButton: this.tableSyncEnabled ? false : this.removeItemButtonValue,
      removeItems: true,
      searchEnabled: this.searchEnabledValue,
      searchPlaceholderValue: this.searchPlaceholderValue,
      itemSelectText: this.itemSelectTextValue,
      classNames: { containerInner: "choices__inner" },
      noResultsText: this.noResultsTextValue,
      noChoicesText: this.noChoicesTextValue
    })
  }

  setupSelectListeners() {
    this.selectTarget.addEventListener("addItem", (event) => {
      if (this.tableSyncEnabled) {
        this.addRowToTable(event.detail)
      }
    })

    this.selectTarget.addEventListener("removeItem", (event) => {
      if (this.tableSyncEnabled && !this.removingFromTable) {
        this.removeRowFromTable(event.detail.value)
      }
    })
  }

  cachePreSelectedRows() {
    if (!this.hasRouteValue) return

    const existingRows = this.tableBodyTarget.querySelectorAll('tr[data-item-id]:not([data-item-id="-1"])')
    existingRows.forEach((row) => {
      const itemId = row.getAttribute("data-item-id")
      const nameEl = row.querySelector("[data-user-name]")
      const emailEl = row.querySelector("[data-user-email]")
      const companyEl = row.querySelector("[data-user-company]")

      if (nameEl && emailEl && companyEl) {
        this.preSelectedData.set(itemId, {
          name: nameEl.getAttribute("data-user-name"),
          email: emailEl.getAttribute("data-user-email"),
          company: companyEl.getAttribute("data-user-company")
        })
      }
    })
  }

  bindModalRemoteLoading() {
    // Shared modal shells use id="modal-…"; Stimulus dropped data-js="modal-container".
    this.modalElement = this.element.closest('[id^="modal-"]')
    if (!this.modalElement) {
      this.setupRemoteLoading()
      return
    }

    this.modalOpenedHandler = () => {
      if (this.remoteLoadingBound) return

      this.setupRemoteLoading()
    }

    this.modalElement.addEventListener("modal:opened", this.modalOpenedHandler)
  }

  unbindModalRemoteLoading() {
    if (this.modalElement && this.modalOpenedHandler) {
      this.modalElement.removeEventListener("modal:opened", this.modalOpenedHandler)
    }

    this.modalElement = null
    this.modalOpenedHandler = null
  }

  setupRemoteLoading() {
    if (this.remoteLoadingBound) return

    const inputElement = this.choices?.input?.element
    if (!inputElement) return

    this.remoteLoadingBound = true

    this.showDropdownHandler = () => {
      this.loadRemoteChoices(inputElement.value || "")
    }
    this.selectTarget.addEventListener("showDropdown", this.showDropdownHandler)

    this.remoteKeyupHandler = (event) => {
      clearTimeout(this.debounceTimeout)
      this.debounceTimeout = setTimeout(() => {
        this.loadRemoteChoices(event.target.value)
      }, this.debounceMsValue)
    }

    inputElement.addEventListener("keyup", this.remoteKeyupHandler)
  }

  teardownRemoteLoading() {
    clearTimeout(this.debounceTimeout)
    this.debounceTimeout = null

    if (this.showDropdownHandler) {
      this.selectTarget.removeEventListener("showDropdown", this.showDropdownHandler)
    }

    if (this.remoteKeyupHandler && this.choices?.input?.element) {
      this.choices.input.element.removeEventListener("keyup", this.remoteKeyupHandler)
    }

    this.showDropdownHandler = null
    this.remoteKeyupHandler = null
    this.remoteLoadingBound = false

    if (this.remoteLoadAbortController) {
      this.remoteLoadAbortController.abort()
      this.remoteLoadAbortController = null
    }

    this.isLoadingRemote = false
  }

  destroyChoicesInstance() {
    if (this.choices) {
      this.choices.destroy()
      this.choices = null
    }
  }

  loadRemoteChoices(searchValue) {
    if (!this.choices || this.isLoadingRemote) return

    const planroomId = this.hasPlanroomIdValue ? this.planroomIdValue : ""
    const role = this.hasRoleValue ? this.roleValue : ""
    const base = this.searchBaseValue.replace(/\/$/, "")
    const url = `${base}/${this.routeValue}?search=${encodeURIComponent(searchValue)}&planroom_id=${encodeURIComponent(planroomId)}&role=${encodeURIComponent(role)}`

    if (this.remoteLoadAbortController) {
      this.remoteLoadAbortController.abort()
    }

    const abortController = new AbortController()
    this.remoteLoadAbortController = abortController
    this.isLoadingRemote = true

    fetch(url, { signal: abortController.signal })
      .then((response) => response.json())
      .then((data) => {
        if (!Array.isArray(data)) {
          console.error("Failed to load choices: unexpected response", data)
          return
        }

        const rawSelected = this.choices.getValue(true)
        const selectedValues = Array.isArray(rawSelected) ? rawSelected : (rawSelected ? [rawSelected] : [])
        const availableChoices = data.filter((item) => {
          const itemId = Number(item.id)
          return !selectedValues.some((value) => Number(value) === itemId)
        })

        this.choices.clearChoices()

        const choicesData = availableChoices.map((item) => ({
          value: item.id,
          label: item.display_name,
          customProperties: {
            name: item.name,
            email: item.email,
            company: item.company
          }
        }))

        this.choices.setChoices(choicesData, "value", "label", false)
      })
      .catch((error) => {
        if (error.name === "AbortError") return

        console.error("Failed to load choices:", error)
      })
      .finally(() => {
        if (this.remoteLoadAbortController !== abortController) return

        this.isLoadingRemote = false
        this.remoteLoadAbortController = null
      })
  }

  addRowToTable(detail) {
    const { value, label, customProperties } = detail

    if (this.tableBodyTarget.querySelector(`tr[data-item-id="${value}"]`)) {
      return
    }

    const useRichTemplate = this.hasRouteValue
    const template = useRichTemplate
      ? (this.hasRichTemplateTarget ? this.richTemplateTarget : null)
      : (this.hasSimpleTemplateTarget ? this.simpleTemplateTarget : null)

    if (!template) {
      console.error("No template available for choices table sync")
      return
    }

    const row = template.content.cloneNode(true).querySelector("tr")
    row.setAttribute("data-item-id", value)
    row.querySelector('[data-action*="removeItem"]').setAttribute("data-value", value)

    if (useRichTemplate) {
      let data = customProperties?.name ? customProperties : null

      if (!data && this.preSelectedData.has(value.toString())) {
        data = this.preSelectedData.get(value.toString())
      }

      if (data) {
        row.querySelector('[data-field="name"]').textContent = data.name
        row.querySelector('[data-field="company"]').textContent = data.company
        row.querySelector('[data-field="email"]').textContent = data.email
      } else {
        row.querySelector('[data-field="name"]').textContent = label
      }
    } else {
      row.querySelector('[data-field="name"]').textContent = label
    }

    this.tableBodyTarget.prepend(row)
    this.toggleEmptyState()
  }

  removeRowFromTable(value) {
    const row = this.tableBodyTarget.querySelector(`tr[data-item-id="${value}"]`)
    if (row) {
      row.remove()
      this.toggleEmptyState()
    }
  }

  toggleEmptyState() {
    const itemCount = this.tableBodyTarget.querySelectorAll('tr[data-item-id]:not([data-item-id="-1"])').length

    if (itemCount === 0) {
      this.tableTarget.classList.add("hidden")
    } else {
      this.tableTarget.classList.remove("hidden")
    }
  }

  removeItem(event) {
    event.preventDefault()
    const value = event.currentTarget.dataset.value

    this.removingFromTable = true
    this.choices.removeActiveItemsByValue(value)
    this.choices.removeActiveItemsByValue(Number(value))
    this.removeRowFromTable(value)
    this.removingFromTable = false
  }
}
